import { supabase, FUNCTIONS_URL, SUPABASE_ANON_KEY } from './config';

// Error thrown for a non-2xx Edge Function response. `body` is the parsed JSON body
// (or null) so callers can surface server-provided details; `status` is the HTTP status.
export class EdgeFunctionError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'EdgeFunctionError';
    this.status = status;
    this.body = body;
  }
}

// Call a Supabase Edge Function as the signed-in user.
//
// Sends the current session's access token so functions can verify the caller, and
// returns the parsed JSON body for any 2xx response (including 207 Multi-Status).
// Non-2xx responses throw EdgeFunctionError; network failures and unparseable bodies
// throw the underlying error. Callers map these onto their own result shapes.
export const callEdgeFunction = async (name, { method = 'POST', body, query, signal, keepalive = false } = {}) => {
  const { data } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token || SUPABASE_ANON_KEY;

  const qs = query ? `?${new URLSearchParams(query).toString()}` : '';

  const response = await fetch(`${FUNCTIONS_URL}/${name}${qs}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    mode: 'cors',
    signal,
    keepalive
  });

  let responseData = null;
  try {
    responseData = await response.json();
  } catch (parseError) {
    if (response.ok) throw parseError;
    // A non-JSON error body comes from the gateway/proxy (e.g. an HTML 502/504 on timeout),
    // not from our function, so the request may still have completed server-side. Throw a
    // plain Error so callers treat it like a network failure: outcome unknown, keep retry keys.
    throw new Error(`${name} failed: ${response.status} (no response from function)`);
  }

  if (!response.ok) {
    throw new EdgeFunctionError(
      responseData?.error || responseData?.message || `${name} failed: ${response.status}`,
      response.status,
      responseData
    );
  }

  return responseData;
};
